import React, { useEffect } from 'react';
import update from 'immutability-helper';
import { useDataStore } from 'files/StateManagement';

import Appearance from 'styles/Appearance.js';
import { LayerItem } from 'structure/Layer.js';

const FieldMapper = ({ resourceID, sections = [], utils }) => {

    const { requiem, setRequiem } = useDataStore();
    const [showSensitiveFields, setShowSensitiveFields] = React.useState(false);

    const onRequiemSmartFlowClick = (item, evt) => {

        evt.stopPropagation();

        // update requiem smart flow state if applicable
        if(requiemSmartFlowEnabled() === true && item?.requiemSmartFlow === true) {
            setRequiem(() => {
                return update(requiem, {
                    smart_flow: {
                        active: {
                            $set: item.key
                        },
                        first_selection: {
                            $set: requiem.smart_flow.first_selection ? false : true
                        },
                        force: {
                            $set: true
                        }
                    }
                })
            });
            return;
        }
    }
    
    const getFields = () => {
        return getSections().reduce((array, section) => {
            return array.concat(section.items.filter(item => item.visible !== false));
        }, []);
    }

    const getSections = () => {
        return sections.filter(section => {
            if(section.visible === false || section.items.length === 0) {
                return false;
            }
            return true;
        });
    }

    const getRequiemSupportedSmartFlowFields = () => {
        return getSections().reduce((array, section) => {
            if(section.authentication?.required === true && section.authentication?.completed === false) {
                return array;
            }
            section.items.forEach(item => {
                if(item.requiemSmartFlow === true) {
                    if(item.component === 'list') {
                        item.description = `${item.description}. Options include: ${item.items.map(item => item.title).join(', ')}`;
                    }
                    array.push(item);
                }
            });
            return array;
        }, []);
    }

    const getRequiemSmartFlowProps = item => {
        if(requiemSmartFlowEnabled() === true && item?.requiemSmartFlow === true) {
            return {
                className: `requiem-shimmer-text ${window.theme}`,
                onClick: onRequiemSmartFlowClick.bind(this, item),
                title: `Click to have Requiem explain ${item.title}`
            }
        }
    }

    const getValue = item => {
        if(!item.value) {
            return null;
        }
        if(item.isSecure === true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1
                }}>
                    {showSensitiveFields === true && (
                        <span style={{
                            ...Appearance.textStyles.value(),
                            flexGrow: 1,
                            ...item.style?.value,
                            ...item.color && {
                                color: item.color
                            }
                        }}>{item.value}</span>
                    )}
                    {showSensitiveFields === false && ( 
                        <span style={{
                            ...Appearance.textStyles.value(),
                            flexGrow: 1,
                            ...item.style?.value,
                            ...item.color && {
                                color: item.color
                            }
                        }}>{'•••••••••••••••'}</span>
                    )}
                    <img
                    className={'cursor-pointer'}
                    onClick={setShowSensitiveFields.bind(this, !showSensitiveFields)}
                    src={showSensitiveFields ? `images/visible-eye-${window.theme === 'dark' ? 'white' : 'grey'}.png` : `images/non-visible-eye-${window.theme === 'dark' ? 'white' : 'grey'}.png`}
                    style={{
                        height: 25,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 25
                    }} />
                </div>
            )
        }
        return (
            <span style={{
                ...Appearance.textStyles.value(),
                flexGrow: 1,
                ...item.style?.value,
                ...item.color && {
                    color: item.color
                }
            }}>{item.value}</span>
        )
    }

    const setupRequiemSmartFlowListener = () => {

        console.log('setting up requiem smart flow listener');

        // set default requiem smart flow object
        setRequiem(() => {
            return update(requiem, {
                smart_flow: {
                    $set: {
                        ...requiem.smart_flow,
                        active: null,
                        completed: false,
                        editable: false,
                        enabled: true,
                        fields: getRequiemSupportedSmartFlowFields().map(field => ({
                            description: field.description,
                            key: field.key,
                            title: field.title
                        })),
                        history: []
                    }
                }
            });
        });
    }

    const updateRequiemSmartFlowListener = () => {

        console.log('updating requiem smart flow listener');

        // set default requiem smart flow object
        setRequiem(() => {
            return update(requiem, {
                smart_flow: {
                    active: {
                        $set: null
                    },
                    fields: {
                        $set: getRequiemSupportedSmartFlowFields().map(field => ({
                            description: field.description,
                            key: field.key,
                            title: field.title
                        }))
                    }
                }
            });
        });
    }

    const requiemSmartFlowEnabled = () => {
        return resourceID && resourceID === requiem.smart_flow?.id && requiem.smart_flow.enabled === true ? true : false;
    }

    useEffect(() => {
        resourceID && resourceID === requiem.smart_flow?.id && setupRequiemSmartFlowListener();
    }, [requiem.smart_flow?.id]);

    useEffect(() => {
        resourceID && resourceID === requiem.smart_flow?.id && updateRequiemSmartFlowListener();
    }, [getFields().length]);

    return getSections().map((section, index, sections) => {
        return (
            <LayerItem
            collapsed={typeof(section.collapsed) === 'boolean' ? section.collapsed : null}
            key={index}
            lastItem={typeof(section.lastItem) === 'boolean' ? section.lastItem : index === sections.length - 1}
            title={section.title}
            utils={utils}>
                <div style={Appearance.styles.unstyledPanel()}>
                    {section.items.filter(item => {
                        return item.visible !== false;
                    }).map((item, index, items) => {
                        return (
                            <div
                            key={index}
                            className={item.onClick ? `view-entry ${window.theme}` : null}
                            onClick={item.onClick?.bind(this, item)}
                            style={{
                                alignItems: 'center',
                                borderBottom: index !== items.length - 1 && Appearance.styles.bottomBorder(),
                                display: 'flex',
                                flexDirection: 'row',
                                padding: '8px 12px 8px 12px',
                                width: '100%'
                            }}>
                                <span 
                                {...getRequiemSmartFlowProps(item)}
                                style={{
                                    ...Appearance.textStyles.key(),
                                    flexShrink: 1,
                                    ...item.style?.key
                                }}>{item.title}</span>
                                {getValue(item)}
                                {typeof(item.onClick) === 'function' && (
                                    <img
                                    src={'images/next-arrow-grey-small.png'}
                                    style={{
                                        height: 12,
                                        marginLeft: 8,
                                        objectFit: 'contain',
                                        width: 12
                                    }} />
                                )}
                            </div>
                        )
                    })}
                    {section.bottomContent}
                </div>
            </LayerItem>
        )
    })
}
export default FieldMapper;
